import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Crown, TrendingUp, BarChart2, Mail, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import { config } from '../utils/config';
import { formatSubscriptionStatus } from '../utils/formatters';
import AccountStatusModal from './AccountStatusModal';

interface Subscription {
  plan_type: string;
  status: string;
  is_trial: boolean;
  trial_end?: string;
  documents_used: number;
  documents_limit: number;
  ai_queries_used: number;
  ai_queries_limit: number;
  invoices_created: number;
  invoices_created_limit: number;
  invoices_edited: number;
  invoices_edited_limit: number;
  invoice_downloads: number;
  invoice_downloads_limit: number;
}

const UserMenu: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });

  useEffect(() => {
    fetchSubscription();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideMenu = menuRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideMenu && !insideDropdown) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const fetchSubscription = async () => {
    try {
      const response = await apiClient.get(config.endpoints.subscription.status);
      setSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const getInitials = () => {
    if (!user) return 'U';
    if (user.full_name) {
      const names = user.full_name.split(' ');
      return names.length > 1
        ? `${names[0][0]}${names[names.length - 1][0]}`
        : names[0][0];
    }
    return user.username[0] || user.email[0] || 'U';
  };

  const getPlanDisplayName = () => {
    if (!subscription) return 'Loading...';
    return subscription.plan_type.charAt(0).toUpperCase() + subscription.plan_type.slice(1).replace('_', ' ');
  };

  const getUpgradeButtonText = () => {
    if (!subscription) return 'View Plans';

    switch (subscription.plan_type) {
      case 'discovery':
        return 'Upgrade Plan';
      case 'essential':
        return 'Upgrade to Advanced';
      case 'advanced':
        return 'View Plans';
      default:
        return 'View Plans';
    }
  };

  const getUpgradeIcon = () => {
    if (!subscription) return <TrendingUp className="w-4 h-4" />;

    switch (subscription.plan_type) {
      case 'discovery':
        return <Sparkles className="w-4 h-4" />;
      case 'essential':
        return <Crown className="w-4 h-4" />;
      case 'advanced':
        return <TrendingUp className="w-4 h-4" />;
      default:
        return <TrendingUp className="w-4 h-4" />;
    }
  };

  const formatTrialEnd = () => {
    if (!subscription?.trial_end) return '';
    const endDate = new Date(subscription.trial_end);
    const now = new Date();
    const daysLeft = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysLeft <= 0) return ' - Expired';
    if (daysLeft === 1) return ' (expires tomorrow)';
    return ` (${daysLeft} days left)`;
  };

  const isTrialExpired = subscription?.status === 'expired' ||
    (subscription?.is_trial && subscription?.trial_end && new Date(subscription.trial_end) < new Date());

  const handleUpgradeClick = () => {
    navigate('/pricing');
    setIsOpen(false);
  };

  const handleAccountStatusClick = () => {
    setShowAccountModal(true);
    setIsOpen(false);
  };

  const handleContactClick = () => {
    navigate('/contact');
    setIsOpen(false);
  };

  return (
    <>
      <div className="relative" ref={menuRef}>
        {/* User Avatar Button */}
        <button
          ref={buttonRef}
          onClick={() => {
            if (!isOpen && buttonRef.current) {
              const rect = buttonRef.current.getBoundingClientRect();
              setDropdownPos({
                top: rect.bottom + 8,
                right: window.innerWidth - rect.right,
              });
            }
            setIsOpen(!isOpen);
          }}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-coral-500/10 hover:bg-coral-500/20 border border-coral-500/20 transition-all"
        >
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-coral-500 to-coral-700 flex items-center justify-center text-white font-semibold text-sm">
            {getInitials()}
          </div>
          <ChevronDown className={`w-4 h-4 text-stone-600 dark:text-foreground-muted transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

      </div>

      {/* Dropdown rendered via portal to escape nav's backdrop-filter stacking context */}
      {isOpen && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={{ position: 'fixed', top: dropdownPos.top, right: dropdownPos.right, zIndex: 9999, maxHeight: `calc(100vh - ${dropdownPos.top + 8}px)` }}
          className="w-72 bg-white dark:bg-background-secondary border border-stone-200 dark:border-coral-500/30 rounded-lg shadow-2xl overflow-y-auto"
        >
          {/* User Info */}
          <div className="px-4 py-3 border-b border-stone-200 dark:border-coral-500/20">
            <p className="text-sm text-stone-600 dark:text-foreground-muted truncate">{user?.email || 'user@example.com'}</p>
          </div>

          {/* Current Plan */}
          <div className="px-4 py-3 bg-stone-50 dark:bg-coral-900/30 border-b border-stone-200 dark:border-coral-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-stone-500 dark:text-foreground-muted mb-0.5">Current Plan</p>
                <p className="text-sm font-semibold text-stone-900 dark:text-white">
                  {getPlanDisplayName()}
                  {subscription?.is_trial && (
                    <span className={`text-xs font-normal ${isTrialExpired ? 'text-red-600 dark:text-red-300 font-semibold' : 'text-coral-600 dark:text-foreground-muted'}`}>
                      {formatTrialEnd()}
                    </span>
                  )}
                </p>
              </div>
              <div className={`px-2 py-1 rounded-full text-xs font-semibold ${
                isTrialExpired
                  ? 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300'
                  : subscription?.status === 'active' || subscription?.status === 'trialing'
                  ? 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-300'
                  : 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300'
              }`}>
                {isTrialExpired
                  ? 'Trial Expired'
                  : subscription ? formatSubscriptionStatus(subscription.status) : 'Loading…'}
              </div>
            </div>
          </div>

          {/* Compact Usage Summary */}
          {subscription && subscription.documents_limit > 0 && (
            <div className="px-4 py-3 border-b border-stone-200 dark:border-coral-500/20">
              <p className="text-xs font-semibold text-stone-500 dark:text-coral-400 uppercase tracking-wide mb-2">
                {subscription.is_trial ? 'Trial Usage' : 'Monthly Usage'}
              </p>
              {[
                { label: 'Documents', used: subscription.documents_used, limit: subscription.documents_limit },
                { label: 'AI Queries', used: subscription.ai_queries_used, limit: subscription.ai_queries_limit },
                { label: 'Invoices', used: subscription.invoices_created, limit: subscription.invoices_created_limit },
                { label: 'Edits', used: subscription.invoices_edited, limit: subscription.invoices_edited_limit },
                { label: 'Downloads', used: subscription.invoice_downloads, limit: subscription.invoice_downloads_limit },
              ].map(({ label, used, limit }) => {
                const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
                const barColor = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-orange-500' : 'bg-coral-500';
                return (
                  <div key={label} className="flex items-center gap-2 mb-2 last:mb-0">
                    <span className="text-xs text-stone-600 dark:text-foreground-muted w-16 shrink-0">{label}</span>
                    <div className="flex-1 bg-stone-200 dark:bg-white/10 rounded-full h-1.5">
                      <div className={`h-1.5 rounded-full transition-all duration-300 ${barColor}`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs text-stone-500 dark:text-coral-400 w-10 text-right shrink-0 tabular-nums">{used}/{limit}</span>
                  </div>
                );
              })}
            </div>
          )}

          <div className="py-1">
            <div className="h-px bg-stone-100 dark:bg-coral-500/10 my-1"></div>
            <button
              onClick={handleUpgradeClick}
              className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors ${
                isTrialExpired
                  ? 'bg-red-500 hover:bg-red-600 text-white font-semibold'
                  : subscription?.plan_type === 'discovery' || subscription?.plan_type === 'essential'
                  ? 'text-purple-600 dark:text-purple-300 hover:bg-stone-100 dark:hover:bg-purple-500/10'
                  : 'text-stone-600 dark:text-coral-400 hover:bg-stone-100 dark:hover:bg-coral-500/10'
              }`}
            >
              {getUpgradeIcon()}
              <span className="text-sm font-medium">{isTrialExpired ? 'Upgrade to Continue' : getUpgradeButtonText()}</span>
            </button>
            <button
              onClick={handleAccountStatusClick}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-stone-600 dark:text-coral-400 hover:bg-stone-100 dark:hover:bg-coral-500/10 transition-colors"
            >
              <BarChart2 className="w-4 h-4" />
              <span className="text-sm">View Account Status</span>
            </button>
            <button
              onClick={handleContactClick}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-stone-600 dark:text-coral-400 hover:bg-stone-100 dark:hover:bg-coral-500/10 transition-colors"
            >
              <Mail className="w-4 h-4" />
              <span className="text-sm">Contact Support</span>
            </button>
          </div>
        </div>,
        document.body!
      )}

      {/* Account Status Modal */}
      <AccountStatusModal
        isOpen={showAccountModal}
        onClose={() => setShowAccountModal(false)}
      />
    </>
  );
};

export default UserMenu;
